import { Grid, Box, Typography, Link } from "@mui/material";
import { Button } from "@covertree/ui";
import { AGENT_HELP_ADDRESS, DISABLED_TOOLTIP_TEXT } from "@consts/index";
import {
  AdditionalInterestCard,
  sortAdditionalInterests,
} from "@covertree/features";

import { EndorsementIneligibleModal } from "@components/EndorsementsModals";

import type { PolicyDetailsContentProps } from "../../types";
import { AdditionalInterestsSkeleton } from "./AdditionalInterestsSkeleton";
import { useEndorsementAdditionalInterests } from "./useEndorsementAdditionalInterests";

export const EndorsementAdditionalInterests = ({
  policy,
  loading,
  error,
}: PolicyDetailsContentProps) => {
  const {
    additionalInterests,
    canManageEndorsements,
    isEligibilityLoading,
    blockingReason,
    handleManageClick,
    handleIneligibleModalClose,
  } = useEndorsementAdditionalInterests(policy);

  if (!loading && error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        width={1}
        gap={2}>
        <Typography
          width={1}
          variant="subtitle1"
          color="black"
          textAlign="center">
          We are unable to load the Additional Interests list at the moment.
          Please try again later. If the issue persists, contact us via email{" "}
          <Link color="secondary" href={`mailto:${AGENT_HELP_ADDRESS}`}>
            {AGENT_HELP_ADDRESS}
          </Link>
          .
        </Typography>
      </Box>
    );
  }

  const manageButton = (
    <Button
      variant="outlined"
      size="small"
      tooltipPlacement="top"
      tooltip={canManageEndorsements ? "" : DISABLED_TOOLTIP_TEXT}
      disabled={!canManageEndorsements || isEligibilityLoading}
      loading={isEligibilityLoading}
      onClick={handleManageClick}>
      Manage Additional Interest
    </Button>
  );

  return (
    <>
      {!policy || loading ? (
        <AdditionalInterestsSkeleton />
      ) : additionalInterests.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          mt={8}>
          <Typography variant="subtitle1" color="text.disabled">
            Nothing here yet
          </Typography>
          {manageButton}
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            {manageButton}
          </Box>
          <Grid container spacing={2}>
            {sortAdditionalInterests(additionalInterests).map(
              (additionalInterest) => (
                <Grid
                  item
                  key={additionalInterest.additionalInterestId}
                  xs={12}>
                  <AdditionalInterestCard
                    fontSize={20}
                    additionalInterest={additionalInterest}
                  />
                </Grid>
              )
            )}
          </Grid>
        </>
      )}

      {!!blockingReason && (
        <EndorsementIneligibleModal
          isModalOpen={!!blockingReason}
          reason={blockingReason}
          onClose={handleIneligibleModalClose}
        />
      )}
    </>
  );
};
